import { Routes, Route } from "react-router-dom";
import LandingPage from "./views/landing";
import Resources from "./views/resources";
import PricingPage from "./views/pricing";
import ContactPage from "./views/contact";
import About from "./views/about";
import AITechnology from "./views/ai-technology";
import Research from "./views/research";
import Process from "./views/process";
import AuthPage from "./views/auth";
import AccountPage from "./views/account";
import AuthCallback from "./views/auth-callback";
import StripeSuccessPage from "./views/stripe-success";
import StripeRedirect from "./components/StripeRedirect";
import SidePanel from "./components/SidePanel";
import ProtectedRoute from "./components/ProtectedRoute";
import ImmersiveAssignment from "./views/ImmersiveAssignment";
import AIGrader from "./components/AIGrader";
import AppShell from "./components/AppShell";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider } from "./contexts/AuthContext";
import { SidePanelProvider } from "./context/SidePanelContext";
import { FloatingPanelProvider } from "./context/FloatingPanelContext";
import { AssignmentProvider } from "./context/AssignmentContext";
import { GeneratedContentProvider } from "./context/GeneratedContentContext";
import { LayoutProvider } from "./context/LayoutContext";
import { NotebookSidebarProvider } from "./context/NotebookSidebarContext";
import { WorkspaceProvider } from "./context/WorkspaceContext";
import { ActiveNoteProvider } from "./context/ActiveNoteContext";
import { SourcesProvider } from "./context/SourcesContext";
import FloatingPanelManager from "./components/FloatingPanelManager";
import { ChakraProvider } from "@chakra-ui/react";
import { Toaster } from "sonner";
import { useMetaPixelPageTracking } from "./hooks/useMetaPixel";
import { useTikTokPixelPageTracking } from "./hooks/useTikTokPixel";

function App() {
  useMetaPixelPageTracking();
  useTikTokPixelPageTracking();

  return (
    <ThemeProvider>
      <AuthProvider>
        <FloatingPanelProvider>
          <GeneratedContentProvider>
            <LayoutProvider>
              <SidePanelProvider>
                <NotebookSidebarProvider>
                  <WorkspaceProvider>
                    <AssignmentProvider>
                      <ActiveNoteProvider>
                        <SourcesProvider>
                          <ChakraProvider>
                            <Routes>
                              {/* Public Routes */}
                              <Route path="/" element={<LandingPage />} />
                              <Route path="/auth" element={<AuthPage />} />
                              <Route
                                path="/auth/callback"
                                element={<AuthCallback />}
                              />
                              <Route path="/about" element={<About />} />
                              <Route
                                path="/contact"
                                element={<ContactPage />}
                              />

                              {/* Protected Routes */}
                              <Route
                                path="/home"
                                element={
                                  <ProtectedRoute>
                                    <AppShell />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/resources"
                                element={
                                  <ProtectedRoute>
                                    <Resources />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/assignment/:groupId"
                                element={
                                  <ProtectedRoute>
                                    <ImmersiveAssignment />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/account"
                                element={
                                  <ProtectedRoute>
                                    <AccountPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/stripe-redirect"
                                element={
                                  <ProtectedRoute>
                                    <StripeRedirect />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/stripe-success"
                                element={
                                  <ProtectedRoute>
                                    <StripeSuccessPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/pricing"
                                element={<PricingPage />}
                              />
                              <Route
                                path="/ai-technology"
                                element={<AITechnology />}
                              />
                              <Route path="/research" element={<Research />} />
                              <Route path="/process" element={<Process />} />
                              <Route
                                path="/account"
                                element={
                                  <ProtectedRoute requireSubscription={false}>
                                    <AccountPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/dashboard/stripe-success"
                                element={
                                  <ProtectedRoute requireSubscription={false}>
                                    <StripeSuccessPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/stripe-redirect"
                                element={
                                  <ProtectedRoute requireSubscription={false}>
                                    <StripeRedirect />
                                  </ProtectedRoute>
                                }
                              />

                              {/* Fully Protected Routes (require both auth and subscription) */}
                              <Route
                                path="/grader"
                                element={
                                  <ProtectedRoute>
                                    <AIGrader />
                                  </ProtectedRoute>
                                }
                              />
                              {/* /notebook → unified AppShell (chat + notebook split view) */}
                              <Route
                                path="/notebook"
                                element={
                                  <ProtectedRoute>
                                    <AppShell />
                                  </ProtectedRoute>
                                }
                              />
                            </Routes>
                            <SidePanel />
                            <FloatingPanelManager />
                            <Toaster position="bottom-right" />
                          </ChakraProvider>
                        </SourcesProvider>
                      </ActiveNoteProvider>
                    </AssignmentProvider>
                  </WorkspaceProvider>
                </NotebookSidebarProvider>
              </SidePanelProvider>
            </LayoutProvider>
          </GeneratedContentProvider>
        </FloatingPanelProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
